// ** Modules
import { useQuery } from '@apollo/client/react';
import { gql, type TypedDocumentNode } from '@apollo/client';

// ** Types
import type { FilterOption } from '@/types/strapi';
import type { Event, CompanyData, SearchCategory, Query } from '@/types/graphql';

/**
 * Generic Helper Type for Query States
 */
type QueryState<K extends string, T> = {
	loading: boolean;
	error: any;
	refetch: () => Promise<any>;
} & { [P in K]: T };

export type EventByIdData = QueryState<'eventById', Event>;
export type EventsData = QueryState<'events', Event[]>;

export type UseGetEventsResult = {
	events: {
		nodes: Event[];
		totalCount: number;
	};
	loading: boolean;
	error: any;
	refetch: () => Promise<any>;
};

export type EventFilters = {
	eventTitle?: { containsi?: string };
	eventCategory?: { eq?: string };
	startDate?: { year?: string };
};

export type EventPagination = {
	page: number;
	pageSize: number;
};

export type GetEventsVariables = {
	filters?: EventFilters;
	pagination?: EventPagination;
};

export type EventDataResult = {
	loading: boolean;
	error: any;
	refetch: () => Promise<any>;
	eventById: Event | null;
	company: CompanyData | null;
};

// --- FRAGMENTS ---

export const EventFragments: TypedDocumentNode<Query> = gql`
    fragment EventCommonFields on Event {
        ## Common Data
        id
        createdAt
        createdBy
        modifiedAt
        modifiedBy

        ## Price Data
        priceTN
        memberPrice
        memberPriceAdditionalPerson
        notMemberPrice
        notMemberPriceAdditionalPerson

        ## Date & Links Data
        start
        startDate
        endDate
        eventLink
        weblink

        ## Relations Data
        xReferent
        teacher {
            id
            person {
                salutation
                title
                name {
                    firstName
                    lastName
                    fullName
                }
            }
        }
        eventPlace
        catering
        contactPerson
        responsible {
            id
            person {
                salutation
                title
                name {
                    firstName
                    lastName
                    fullName
                }
                contactData {
                    email
                    phone
                }
            }
        }

        ## Note Data
        description
        title
        groupTarget
        target
        content
        annotation
        line
        appointmentInfo
        memberFeeNote
        notMemberFeeNote

        ## Numbers Data
        numberOfDays
        actualNumberOfParticipants
        minParticipantsNumber
        maxParticipantsNumber

        ## Text data
        internalNotes
        eventName
        status
        timeOfDay
        field
        eventType
        eventNumber
        subtitle
        priceCondition

        ## Checks Data
        duration
        cateringBooked
        placeBooked
        priceWithoutTax
        publishedOnline
        actualItem
        onlyForMembers
    }
`;

export const CompanyFields: TypedDocumentNode<Query> = gql`
    fragment CompanyFields on CompanyData {
        id
        company {
            companyName
            address {
                street
                houseNumber
                postalCode
                city
                stateCode
            }
            contactData {
                phone
                mobilePhone
                email
            }
        }
    }
`;

// --- MUTATIONS ---

export const postEventGuestRequestMutation: TypedDocumentNode<Query> = gql`
    mutation postEventGuestRequest($data: GuestRequestInput!) {
        eventGuestRequest(data: $data) {
            success
            message
            event {
                id
            }
        }
    }
`;

export const postEventUserRequestMutattion: TypedDocumentNode<Query> = gql`
    mutation postEventUserRequest($data: UserRequestInput!) {
        eventUserRequest(data: $data) {
            booking {
                eventId
                createdAt
                participants {
                    firstName
                    lastName
                    email
                }
            }
            errors {
                field
                message
            }
        }
    }
`;

// --- QUERIES ---

export const getUserEventsQuery: TypedDocumentNode<Query> = gql`
    ${EventFragments}
    query getUserEvents($id: ID!) {
        userEvents(id: $id) {
            ...EventCommonFields
        }
    }
`;

/**
 * Unified Event Query: Backend handles both numeric ID and String Name/Slug
 */
export const getEventByIdQuery: TypedDocumentNode<Query> = gql`
    ${EventFragments}
    query getEventById($id: ID!) {
        eventById(id: $id) {
            ...EventCommonFields
        }
    }
`;

export const getEventsCategories: TypedDocumentNode<Query> = gql`
    query GetEventCategories {
        allEventsCategories {
            name
            value
        }
    }
`;

export const getEventsQuery: TypedDocumentNode<Query> = gql`
    ${EventFragments}
    query getEvents(
        $limit: Int, 
        $page: Int, 
        $searchTerm: String, 
        $category: String, 
        $period: String
    ) {
        events(
            limit: $limit, 
            page: $page, 
            searchTerm: $searchTerm, 
            category: $category, 
            period: $period
        ) {
            nodes {
                ...EventCommonFields
            }
            totalCount
        }
    }
`;

export const getEventData: TypedDocumentNode<Query> = gql`
    ${CompanyFields}
    ${EventFragments}
    query GetEventData($eventId: ID!, $companyId: ID!, $hasCompany: Boolean!) {
        eventById(id: $eventId) {
            ...EventCommonFields
        }
        company(companyId: $companyId) @include(if: $hasCompany) {
            ...CompanyFields
        }
    }
`;

// --- HOOKS ---

/**
 * Hook for fetching a single user's events
 */
export const useGetUserEvents = (id: number) => {
	const props = useQuery(getUserEventsQuery, {
		variables: { id: String(id) },
		skip: !id
	});

	const userEvents = props.data?.userEvents ?? [];

	return {
		...props,
		userEvents
	};
};

/**
 * Unified Hook for Event Details
 * Accepts numeric ID or slug (e.g., 'event-name-2024')
 */
export const useGetEventById = (id: number | string): EventByIdData => {
	const shouldSkip = !id;

	const { loading, error, data, refetch } = useQuery(getEventByIdQuery, {
		variables: { id: String(id) },
		skip: shouldSkip
	});

	if (!loading && !error && !data?.eventById && !shouldSkip) {
		throw new Error(`Event not found for identifier: ${id}`);
	}

	return {
		loading,
		error,
		refetch,
		eventById: data?.eventById ?? ({} as Event)
	};
};

/**
 * Main Hook for Paginated/Filtered Event Listings
 */
export const useGetEvents = ({ pagination, filters }: GetEventsVariables): UseGetEventsResult => {
	const { data, loading, error, refetch } = useQuery(getEventsQuery, {
		variables: {
			limit: pagination?.pageSize ?? 8,
			page: pagination?.page ?? 1,
			searchTerm: filters?.eventTitle?.containsi || null,
			category: filters?.eventCategory?.eq || 'all',
			period: filters?.startDate?.year || 'all'
		},
		notifyOnNetworkStatusChange: true
	});

	const rawNodes = data?.events?.nodes || [];
	const cleanNodes = rawNodes.filter(Boolean) as Event[];

	return {
		events: {
			nodes: cleanNodes,
			totalCount: data?.events?.totalCount ?? 0
		},
		loading,
		error,
		refetch
	};
};

/**
 * Provides dropdown options for categories
 */
export const useGetEventCategories = ({ allLabel }: { allLabel?: string | null }) => {
	const { data, loading, error } = useQuery(getEventsCategories);

	const categoryData = data?.allEventsCategories as SearchCategory[];

	const categories: FilterOption[] = [
		{ label: allLabel || 'All Categories', value: 'all' },
		...(categoryData
			?.filter((cat) => cat.name && cat.value)
			.map((cat) => ({
				label: cat.name ?? 'Unknown',
				value: cat.value ?? ''
			})) || [])
	];

	return { categories, loading, error };
};

/**
 * Static provider for time-period filters
 */
export const useGetEventPeriods = ({ allLabel }: { allLabel?: string | null }) => {
	const periods: FilterOption[] = [
		{ label: allLabel || 'All Upcoming', value: 'all' },
		{ label: '7 Days', value: '7_days' },
		{ label: '14 Days', value: '14_days' },
		{ label: '1 Month', value: '30_days' },
		{ label: '3 Months', value: '90_plus' }
	];

	return { periods, loading: false };
};

/**
 * Combined Hook for fetching an Event and an optional Company
 */
export const useGetEventData = ({
	eventId,
	companyId
}: {
	eventId: number | string;
	companyId?: number | string | null;
}): EventDataResult => {
	const hasCompany = !!companyId && companyId !== '0' && companyId !== 0;
	const shouldSkip = !eventId;

	const { loading, error, data, refetch } = useQuery(getEventData, {
		variables: {
			eventId: String(eventId),
			companyId: hasCompany ? String(companyId) : '0',
			hasCompany
		},
		skip: shouldSkip
	});

	if (loading || shouldSkip || error) {
		return {
			loading,
			error: error || null,
			refetch,
			eventById: data?.eventById ?? null,
			company: data?.company ?? null
		};
	}

	return {
		loading: false,
		error: null,
		refetch,
		eventById: data?.eventById ?? null,
		company: data?.company ?? null
	};
};
